import { Grid, Group, Layout, Page } from "@/comn/components";
import { useNavigate } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useTheme, useToast } from "@/comn/hooks";
import { APIS, SF_ROLE_MGMT_DTL, SG_ROLE_LANG_LIST, URLS } from "@/tra/tancis/ptli/ptl/role/services/PtliRoleService";
import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import { PTLI1016004Q } from "@/tra/tancis/ptli/ptl/roleauth/PTLI1016004Q";
import { SG_ROLE_AUTH_CNCT_LIST } from "@/tra/tancis/ptli/ptl/roleauth/services/PtliRoleAuthService";
import { PTLI1016003Q } from "@/tra/tancis/ptli/ptl/roleauth/PTLI1016003Q";

/*
 * Register for Role Management
 * !== 역할 관리 등록 ==!
 */
export const PTLI1004002S = (): JSX.Element => {
    const {} = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const navigate = useNavigate();
    const modal = useModal();
    const toast = useToast();
    const { theme } = useTheme();
    const { t } = useTranslation();
    const langCodes = ["KO", "EN", "TZ"];

    useResource({ defaultSchema: [{ area: "comnCd", comnCd: "COM0052" }] });

    const form = { roleDtl: useForm({ defaultSchema: SF_ROLE_MGMT_DTL }) };

    const grid = {
        roleLangList: useGrid({ defaultSchema: SG_ROLE_LANG_LIST }),
        roleAuthList: useGrid({ defaultSchema: SG_ROLE_AUTH_CNCT_LIST }),
    };

    const fetch = {
        createRoleDtl: useFetch({
            api: () =>
                APIS.createRoleDtl({
                    ...form.roleDtl.getValues(),
                    roleLangList: grid.roleLangList.getData(),
                    cnctList: grid.roleAuthList.getData(),
                }),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00058",
                    onClose: () => handler.onListBtnClick(),
                }); // Successfully inserted. !== 정상적으로 등록되었습니다. ==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),
    };

    const render = {
        roleAuthList: {
            cell: {
                authRelaMenuCnt: (args: any) => {
                    const { rowValues, value } = args;
                    return (
                        <button
                            className="underline"
                            onClick={() => {
                                modal.openModal({
                                    draggable: true,
                                    size: "sm",
                                    content: (
                                        <PTLI1016004Q
                                            ptlTpCd={form.roleDtl.getValues("ptlTpCd")}
                                            authId={rowValues.authId}
                                        />
                                    ),
                                });
                            }}
                        >
                            {value}
                        </button>
                    );
                },
            },
        },
    };

    const handler = {
        onGridAddBtnClick: () => {
            const modalCallback = (list: any[]) => {
                !comnUtils.isEmpty(list) && list.forEach((item: any) => grid.roleAuthList.addRow({ ...item }));
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "xl",
                content: (
                    <PTLI1016003Q
                        roleId={"new"}
                        ptlTpCd={form.roleDtl.getValue("ptlTpCd")}
                        callback={modalCallback}
                        roleAuthList={grid.roleAuthList.getData()}
                    />
                ),
            });
        },
        onGridDelBtnClick: () => {
            if (!grid.roleAuthList.isChecked()) {
                modal.openModal({ content: "msg.ptl.00683" }); // Please select a destination to delete. !== 삭제대상을 선택하세요. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.roleAuthList.deleteRow("checkbox");
                },
            });
        },
        onListBtnClick: () => navigate(`${URLS.ptli1004001q}`),
        onSubmitBtnClick: () => {
            form.roleDtl.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            grid.roleLangList.clearData();

                            langCodes.forEach((langCd: any) => {
                                const roleNm = form.roleDtl.getValues(`roleNm${langCd}`);
                                const roleDesc = form.roleDtl.getValues(`roleDesc${langCd}`);

                                if (!comnUtils.isEmpty(roleNm) || !comnUtils.isEmpty(roleDesc)) {
                                    grid.roleLangList.addRow({ langCd: langCd, roleNm: roleNm, roleDesc: roleDesc });
                                }
                            });

                            fetch.createRoleDtl.fetch();
                        },
                    });
                },
                () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            )();
        },
    };

    useEffect(() => {
        form.roleDtl.setValue("roleId", t("msg.ptl.00700"));
    }, [theme.lang]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmTZ} />
                            <Group.Control {...form.roleDtl.schema.roleDescTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmEN} />
                            <Group.Control {...form.roleDtl.schema.roleDescEN} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmKO} />
                            <Group.Control {...form.roleDtl.schema.roleDescKO} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section hidden={true}>
                        <Grid {...grid.roleLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Layout>
                            <Layout.Right>
                                <Layout.Button role={"gridAdd"} onClick={handler.onGridAddBtnClick} />
                                <Layout.Button role={"gridDelete"} onClick={handler.onGridDelBtnClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Section>
                    <Group.Section>
                        <Grid {...grid.roleAuthList.grid} render={render.roleAuthList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={() => handler.onListBtnClick()} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"submit"} onClick={handler.onSubmitBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
